import { STEP, VALUE } from "./propertyNames";
import { toArray } from "./reducers";
import { isReduced } from "./util";
var ITERATOR_SYMBOL = typeof Symbol !== "undefined" ? Symbol.iterator : "@@iterator";
/**
 * For compatibility with environments where common types aren't iterable.
 */
export function getIterator(collection) {
    var anyCollection = collection;
    if (anyCollection[ITERATOR_SYMBOL]) {
        return anyCollection[ITERATOR_SYMBOL]();
    }
    else if (Array.isArray(anyCollection) ||
        typeof anyCollection === "string") {
        // Treat a string like an array of characters.
        return new ArrayIterator(anyCollection);
    }
    else {
        throw new Error("Cannot get iterator of non iterable value: " + anyCollection);
    }
}
/**
 * Iterator for arrays in environments without Iterable.
 */
var ArrayIterator = /**@__PURE__*/(function () {
    function ArrayIterator(array) {
        this.array = array;
        this.i = 0;
    }
    ArrayIterator.prototype[ITERATOR_SYMBOL] = function () {
        return this;
    };
    ArrayIterator.prototype.next = function () {
        var _a = this, i = _a.i, array = _a.array;
        if (i < array.length) {
            this.i++;
            return { done: false, value: array[i] };
        }
        else {
            return { done: true };
        }
    };
    return ArrayIterator;
}());
var RangeIterator = /**@__PURE__*/(function () {
    function RangeIterator(startOrEnd, end, step) {
        if (end == null) {
            this.i = 0;
            this.end = startOrEnd;
            this.step = 1;
        }
        else if (step == null) {
            this.i = startOrEnd;
            this.end = end;
            this.step = 1;
        }
        else {
            this.i = startOrEnd;
            this.end = end;
            this.step = step;
        }
    }
    RangeIterator.prototype[ITERATOR_SYMBOL] = function () {
        return this;
    };
    RangeIterator.prototype.next = function () {
        var _a = this, i = _a.i, end = _a.end, step = _a.step;
        if ((step > 0 && i < end) || (step < 0 && i > end)) {
            var result = { done: false, value: i };
            this.i += step;
            return result;
        }
        else {
            return { done: true };
        }
    };
    return RangeIterator;
}());
export function range(startOrEnd, end, step) {
    var _a;
    if (step === 0) {
        throw new Error("Step in range() cannot be 0");
    }
    return _a = {},
        _a[ITERATOR_SYMBOL] = function () { return new RangeIterator(startOrEnd, end, step); },
        _a;
}
var RepeatIterator = /**@__PURE__*/(function () {
    function RepeatIterator(value, count) {
        if (count === void 0) { count = Number.POSITIVE_INFINITY; }
        this.value = value;
        this.count = count;
        this.i = 0;
    }
    RepeatIterator.prototype[ITERATOR_SYMBOL] = function () {
        return this;
    };
    RepeatIterator.prototype.next = function () {
        if (this.count == null || this.i < this.count) {
            this.i++;
            return { done: false, value: this.value };
        }
        else {
            return { done: true };
        }
    };
    return RepeatIterator;
}());
export function repeat(value, count) {
    var _a;
    if (count != null && count < 0) {
        throw new Error("Repeat count cannot be negative");
    }
    return _a = {}, _a[ITERATOR_SYMBOL] = function () { return new RepeatIterator(value, count); }, _a;
}
var IterateIterator = /**@__PURE__*/(function () {
    function IterateIterator(initialValue, getNextValue) {
        this.getNextValue = getNextValue;
        this.hasEmittedInitialValue = false;
        this.lastValue = initialValue;
    }
    IterateIterator.prototype[ITERATOR_SYMBOL] = function () {
        return this;
    };
    IterateIterator.prototype.next = function () {
        if (!this.hasEmittedInitialValue) {
            this.hasEmittedInitialValue = true;
            return { done: false, value: this.lastValue };
        }
        else {
            this.lastValue = this.getNextValue(this.lastValue);
            return { done: false, value: this.lastValue };
        }
    };
    return IterateIterator;
}());
export function iterate(initialValue, getNextValue) {
    var _a;
    return _a = {},
        _a[ITERATOR_SYMBOL] = function () {
            return new IterateIterator(initialValue, getNextValue);
        },
        _a;
}
var CycleIterator = /**@__PURE__*/(function () {
    function CycleIterator(values) {
        this.values = [];
        this.hasConsumedIterator = false;
        this.i = 0;
        this.valueIterator = values[ITERATOR_SYMBOL]();
    }
    CycleIterator.prototype[ITERATOR_SYMBOL] = function () {
        return this;
    };
    CycleIterator.prototype.next = function () {
        if (!this.hasConsumedIterator) {
            var result = this.valueIterator.next();
            var done = result.done, value = result.value;
            if (!done) {
                this.values.push(value);
                return result;
            }
            else {
                this.hasConsumedIterator = true;
                return this.next();
            }
        }
        else if (this.values.length === 0) {
            return { done: true };
        }
        else {
            var value = this.values[this.i];
            this.i = (this.i + 1) % this.values.length;
            return { done: false, value: value };
        }
    };
    return CycleIterator;
}());
export function cycle(values) {
    var _a;
    return _a = {}, _a[ITERATOR_SYMBOL] = function () { return new CycleIterator(values); }, _a;
}
/**
 * An iterable which enables lazy consumption of the output of a
 * transducer-based transform.
 */
var TransducerIterable = /**@__PURE__*/(function () {
    function TransducerIterable(iterator, xf) {
        this.iterator = iterator;
        this.upcoming = new ArrayIterator([]);
        this.hasSeenEnd = false;
        this.xfToArray = xf(toArray());
    }
    TransducerIterable.prototype[ITERATOR_SYMBOL] = function () {
        return this;
    };
    TransducerIterable.prototype.next = function () {
        while (true) {
            var backlogged = this.upcoming.next();
            if (!backlogged.done) {
                return backlogged;
            }
            else if (this.hasSeenEnd) {
                return { done: true };
            }
            else {
                var _a = this.iterator.next(), done = _a.done, value = _a.value;
                if (done) {
                    return { done: done };
                }
                else {
                    var outValues = this.xfToArray[STEP]([], value);
                    if (isReduced(outValues)) {
                        this.hasSeenEnd = true;
                        outValues = outValues[VALUE];
                    }
                    this.upcoming = new ArrayIterator(outValues);
                }
            }
        }
    };
    return TransducerIterable;
}());
export function lazyTransduce(collection, transform) {
    // We can't satisfy the IterableIterator interface while functioning in
    // environments without Symbol, hence the cast.
    return new TransducerIterable(getIterator(collection), transform);
}
//# sourceMappingURL=iterables.js.map